import React from 'react';
import { MultiSelectFilter } from '@hud/web-components/Filters/Filters';
import { useDebouncedValue, useInputState } from '@mantine/hooks';
import { DEFAULT_DEBOUNCE_TIME } from '../../consts';
import useAnalytics from '../../hooks/useAnalytics';
import { formatDateTime } from '@hud/web-components/utils';
import { FlowDeployment } from '@hud/portal-dtos/webapp-endpoint.dto';

function getFilterLabel(
  selectedFlowDeployments: string[] | undefined,
  filteredDeploymentsList: { deployment: string }[],
): string {
  if (selectedFlowDeployments && selectedFlowDeployments.length === 1) {
    const singleSelectedDeployment = filteredDeploymentsList?.find(
      ({ deployment }) => deployment === selectedFlowDeployments[0],
    );
    return singleSelectedDeployment
      ? formatDateTime(new Date(parseInt(singleSelectedDeployment.deployment)))
      : 'Select...';
  }

  return selectedFlowDeployments && selectedFlowDeployments.length > 1
    ? `${selectedFlowDeployments.length} filtered`
    : 'Select…';
}

export default function FlowDeploymentsFilter({
  deployments,
  filteredFlowDeployments: selectedFlowDeployments,
  onFilteredFlowDeploymentsChange,
  handleFilterChange,
}: {
  deployments: FlowDeployment[];
  filteredFlowDeployments?: string[];

  onFilteredFlowDeploymentsChange?: (value: string[]) => void;
  handleFilterChange: (
    filterType: 'deployment',
    isActive: boolean,
    filterProps: object,
  ) => void;
}) {
  const analytics = useAnalytics();

  const [flowDeploymentSearchValue, setFlowDeploymentSearchValue] = useInputState('');

  const [debouncedFlowDeploymentSearchValue] = useDebouncedValue(
    flowDeploymentSearchValue,
    DEFAULT_DEBOUNCE_TIME,
  );

  const debouncedSearchTerms = {
      searchTerms: debouncedFlowDeploymentSearchValue.split(' ').filter(Boolean),
    };

  const FilteredDeploymentsList = deployments.filter((deployment) => {
    const timestampStr = deployment.timestamp.getTime().toString();
    const isSelected =
    selectedFlowDeployments &&
      timestampStr in selectedFlowDeployments;

    return isSelected || isMatchSearchTerms(deployment, debouncedSearchTerms.searchTerms);
  })
  .map((deployment) => ({
    deployment: deployment.timestamp.getTime().toString(),
  }));

  const toggleFlowDeploymentFilter = (isActive: boolean) => {
    handleFilterChange('deployment', isActive, { deployments: selectedFlowDeployments });
  };

  const filterLabel = getFilterLabel(selectedFlowDeployments, FilteredDeploymentsList);

  return (
    <MultiSelectFilter
      label="Deployed at"
      key="deployment"
      options={
        FilteredDeploymentsList?.map(({ deployment }) => ({
          value: deployment,
          label: formatDateTime(new Date(parseInt(deployment))),
        })) ?? []
      }
      onClear={() => {
        onFilteredFlowDeploymentsChange?.([]);
        toggleFlowDeploymentFilter(false);
        analytics.capture('flow_deployments_filter_removed');
      }}
      onChange={(value: string[]) => {
        onFilteredFlowDeploymentsChange?.(value);
      }}
      onSearch={setFlowDeploymentSearchValue}
      searchTerm={flowDeploymentSearchValue}
      onClose={() => setFlowDeploymentSearchValue('')}
      multiSelectLabel={filterLabel}
      selectedOptions={selectedFlowDeployments?.map((d) => d.toString()) ?? []}
    />
  );
}

function isMatchSearchTerms(deployment: FlowDeployment, searchTerms: string[]): boolean {
  if (!searchTerms.length) {
    return true;
  }
  return searchTerms.every((term) =>
    formatDateTime(deployment.timestamp).includes(term)
  );
}
